import React, { useState, useEffect, useMemo, useCallback } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useContentToken,
  useSlotGroup,
  useProperty,
  usePrimaryColor,
  usePropertyAddressUrl,
  useCollectionBinding,
} from '@vibesites/runtime/react';
import { EditableToken, EditableSlotGroup } from '@vibesites/runtime/react';
// Use the fully-merged page catalog (theme chain + this property's overrides),
// NOT the local `pages.json` override stub. The inherited Header read the
// theme's complete pages.json; for a property-level fork the equivalent full
// list lives in vibesites-pages.json. Importing the stub instead would collapse
// the sidebar navigation to just "Home".
import pagesData from '../vibesites-pages.json';
// Source-level page overrides (`src/pages.json`). Used ONLY to resolve
// per-page `visible` flags, layered on top of the baked catalog above. This
// lets a source `visible` change (e.g. enabling `apply-now`) take effect in
// preview before the deploy pipeline re-bakes `vibesites-pages.json`, without
// replacing `pagesData` for navigation (which would collapse the sidebar to
// just "Home", since the stub carries only the forked pages).
import pagesOverride from '../pages.json';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { getContrastTextColor, TEXT_ON_PRIMARY_LIGHT } from '../utils/luminance';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFacebookF,
  faInstagram,
  faXTwitter,
  faYoutube,
  faTiktok,
  faYelp,
  faGoogle,
} from '@fortawesome/free-brands-svg-icons';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';

// ============================================================================
// Knock Doorway (inline tour-scheduling slideout) integration
// ----------------------------------------------------------------------------
// The knock-chatbot block is mounted globally on every page, so when
// `window.knockDoorway` exists at click time the user expects the inline
// slideout — never a redirect. We commit to that aggressively: as soon as we
// detect knockDoorway we kill the anchor's default action AND stop propagation
// up front, before any other listener (including the chatbot block's own
// document-level delegate) sees the click. This is what prevents the
// "popup opens AND page redirects" double-fire.
//
// Load-order detail: doorway.min.js is just a *loader*. The real implementation
// (`openScheduling`) is dynamically injected and isn't ready until
// `knockDoorway.isLoaded === true`. Robust pattern: call `openScheduling`
// directly when loaded, else queue via `addOnLoadHandler`.
//
// Behavior matrix:
//   * knockDoorway absent      -> follow href as usual (e.g. RealPage sites)
//   * present, isLoaded        -> preventDefault, openScheduling()
//   * present, not yet loaded  -> preventDefault, queue via addOnLoadHandler
//   * present, no usable API   -> preventDefault anyway (keep user on page)
// ============================================================================

interface KnockDoorway {
  /** True once the v3 bundle has finished injecting and the API is usable. */
  isLoaded?: boolean;
  /** Opens the inline scheduling slideout popup. */
  openScheduling?: () => void;
  /** Queues a callback to run the moment the bundle finishes loading. */
  addOnLoadHandler?: (handler: () => void) => void;
}

declare global {
  interface Window {
    knockDoorway?: KnockDoorway;
  }
}

// ============================================================================
// Dynamic Navigation from pages.json
// ============================================================================

interface PageEntry {
  name: string;
  path: string;
  position: number;
  visible?: boolean;
  template?: string;
  noindex?: boolean;
  /** When set, this page is rendered as a sub-nav item under the parent
      page's nav entry (indented row in the sidebar). */
  parent?: string;
}

/** Page aliases that should never appear in the main navigation. Note `home`
    is intentionally NOT excluded — the sidebar surfaces an explicit "Home"
    entry at the top of the link list (see buildNavLinks). */
const NAV_EXCLUDED_ALIASES = new Set([
  'home',
  'privacy-policy',
  'sitemap',
  'accessibility',
  'apply-now',
  'thank-you',
  'resident-access',
  'schedule-a-tour',
  'hyperlocal',
  'reviews',
]);

interface NavLink {
  label: string;
  href: string;
  /** Sub-nav items rendered as indented rows under their parent in the
      sidebar. Populated when other pages declare `parent: <this alias>`. */
  children?: NavLink[];
}

interface VirtualTour {
  virtual_tour_url?: string | null;
}

/** Canonical Contact Us page path. When the header's "Schedule a Tour" CTA is
    pointed at this same path, we drop the inherited Contact Us nav entry so the
    sidebar doesn't surface two competing contact-us CTAs. */
const CONTACT_US_PATH = '/contact-us';

/** Normalize a path for comparison: trim, lowercase, and strip a single
    trailing slash (but keep the root "/"). Lets "/Contact-Us/" match
    "/contact-us". */
function normalizePath(raw: string | null | undefined): string {
  const trimmed = (raw || '').trim().toLowerCase();
  if (trimmed.length > 1 && trimmed.endsWith('/')) return trimmed.slice(0, -1);
  return trimmed;
}

/**
 * Build navigation links from pages.json.
 * Only includes pages that are visible and not in the exclusion list.
 * Sorted by position. Pages declaring `parent: <alias>` are nested as
 * `children` of their parent entry. A synthetic "Home" entry is prepended
 * so the sidebar mirrors the screenshot (HOME first, then the rest).
 *
 * `hasInteractiveMap` controls the dynamic visibility of the Interactive
 * Map page (Engrain Sightmap embed keyed by a per-property sightmap ID); if
 * no Engrain sitemap ID is available the page drops out of the nav, mirroring
 * the SSG-side hide + noindex logic that turns the page off entirely.
 *
 * `hideContactNav` drops the inherited Contact Us nav entry (path
 * `/contact-us`). This is set when the "Schedule a Tour" CTA already links to
 * `/contact-us`, so the sidebar doesn't render two duplicate contact-us CTAs.
 */
function buildNavLinks(
  hasVirtualTours: boolean,
  hasInteractiveMap: boolean,
  hideContactNav: boolean,
): NavLink[] {
  const eligible = Object.entries(pagesData as unknown as Record<string, PageEntry>)
    .filter(([alias, page]) => {
      if (NAV_EXCLUDED_ALIASES.has(alias)) return false;
      if (page.visible === false) return false;
      if (page.template) return false;
      if (alias === 'virtual-tour' && !hasVirtualTours) return false;
      if (alias === 'interactive-map' && !hasInteractiveMap) return false;
      if (hideContactNav && normalizePath(page.path) === CONTACT_US_PATH) return false;
      return true;
    })
    .sort(([, a], [, b]) => a.position - b.position);

  const eligibleAliases = new Set(eligible.map(([alias]) => alias));
  const linksByAlias = new Map<string, NavLink>();
  const topLevel: NavLink[] = [];

  for (const [alias, page] of eligible) {
    linksByAlias.set(alias, { label: page.name, href: page.path });
  }

  for (const [alias, page] of eligible) {
    const link = linksByAlias.get(alias)!;
    if (page.parent && eligibleAliases.has(page.parent) && page.parent !== alias) {
      const parentLink = linksByAlias.get(page.parent)!;
      (parentLink.children ??= []).push(link);
    } else {
      topLevel.push(link);
    }
  }

  // Prepend an explicit Home entry pointing at the site root.
  return [{ label: 'Home', href: '/' }, ...topLevel];
}

// ============================================================================
// Office hours helpers (mirrors the Footer's grouping logic)
// ============================================================================

interface OfficeHour {
  day?: string | null;
  open_time?: string | null;
  close_time?: string | null;
}

interface GroupedHours {
  label: string;
  hours: string;
}

const DAY_ORDER = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const DAY_LABELS: Record<string, string> = {
  mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu', fri: 'Fri', sat: 'Sat', sun: 'Sun',
};

function hasValue(val: string | null | undefined): val is string {
  if (!val) return false;
  const normalized = val.trim().toLowerCase();
  return normalized !== '' && normalized !== 'false' && normalized !== 'null' && normalized !== 'undefined';
}

function normalizeDay(day: string): string {
  return day.trim().toLowerCase().slice(0, 3);
}

function formatTime(raw: string): string {
  const trimmed = raw.trim();
  if (/[ap]m/i.test(trimmed)) {
    return trimmed.replace(/([ap]m)/i, (m) => m.toLowerCase());
  }
  const match = trimmed.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return trimmed;
  let hours = parseInt(match[1], 10);
  const minutes = match[2];
  const period = hours >= 12 ? 'pm' : 'am';
  if (hours === 0) hours = 12;
  else if (hours > 12) hours -= 12;
  return `${hours}:${minutes} ${period}`;
}

function formatPhone(raw: string | null | undefined): string {
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  const d = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (d.length !== 10) return raw;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

/** Groups consecutive days that share the same hours into a single labelled
    range (e.g. "Mon - Fri"). Days with no open/close time render as "Closed". */
function groupOfficeHours(raw: OfficeHour[]): GroupedHours[] {
  const sorted = [...raw].sort((a, b) => {
    const ai = DAY_ORDER.indexOf(normalizeDay(a.day || ''));
    const bi = DAY_ORDER.indexOf(normalizeDay(b.day || ''));
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
  });

  const groups: GroupedHours[] = [];

  for (const entry of sorted) {
    if (!hasValue(entry.day)) continue;
    const key = normalizeDay(entry.day);
    const dayLabel = DAY_LABELS[key] || (entry.day || '').slice(0, 3);
    const isClosed = !hasValue(entry.open_time) && !hasValue(entry.close_time);
    const hoursStr = isClosed
      ? 'Closed'
      : `${formatTime(entry.open_time || '')} - ${formatTime(entry.close_time || '')}`;

    const prev = groups[groups.length - 1];
    if (prev && prev.hours === hoursStr) {
      const dash = prev.label.indexOf(' - ');
      const startLabel = dash !== -1 ? prev.label.slice(0, dash) : prev.label;
      prev.label = `${startLabel} - ${dayLabel}`;
    } else {
      groups.push({ label: dayLabel, hours: hoursStr });
    }
  }

  return groups;
}

// ============================================================================
// Slot Group Definitions
// ============================================================================

const logoSlotDef: SlotGroupDefinition = {
  label: 'Logo',
  fields: {
    image: {
      label: 'Logo Image',
      type: 'image',
      defaultField: 'property_mono_white_logo',
      suggestedFields: ['property_mono_white_logo', 'property_primary_logo', 'property_mono_black_logo'],
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Community Logo' },
  },
};

const applyNowLinkDef: SlotGroupDefinition = {
  label: 'Apply Now Link',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_apply_now_url',
      suggestedFields: ['property_apply_now_url', 'property_website_url'],
    },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Apply Now' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const scheduleTourLinkDef: SlotGroupDefinition = {
  label: 'Schedule a Tour Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/schedule-a-tour' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Schedule a Tour' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const residentAccessLinkDef: SlotGroupDefinition = {
  label: 'Resident Access Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/resident-access' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Resident Access' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const addressLinkDef: SlotGroupDefinition = {
  label: 'Address Link',
  fields: {
    address: {
      label: 'Address',
      type: 'text',
      defaultField: 'property_full_address',
      defaultValue: '123 Main Street, Atlanta, GA 30313',
    },
    // URL has no defaultField — defaults to usePropertyAddressUrl() at render
    // time (mobile-safe Google Maps URL). An explicit editor override wins.
    url: { label: 'URL', type: 'url' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

// ============================================================================
// SVG Icon Components
// ============================================================================

function MapPinIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );
}

interface SocialEntry {
  url: string;
  icon: IconDefinition;
  label: string;
}

// ============================================================================
// Main Header Component — fixed left sidebar (desktop) / slide-out sheet
// (mobile, behind a hamburger top bar).
// ============================================================================

export function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const closeMobileMenu = () => setMenuOpen(false);

  // ---- Knock Doorway tour-scheduling popup (presence-gated) ----
  // Intercepts clicks on the "Schedule a Tour" link whenever `window.knockDoorway`
  // exists at click time. When present we block navigation on every layer
  // (synthetic, native, and any document-level delegates) before opening the
  // inline slideout; when absent the click is left untouched so the browser
  // follows the href.
  const handleScheduleTourClick = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>) => {
      const doorway = typeof window !== 'undefined' ? window.knockDoorway : undefined;

      if (doorway) {
        // Block navigation on every layer — synthetic, native, and any
        // document-level delegates. This is what stops the
        // "popup opens AND page redirects" double-fire.
        event.preventDefault();
        event.stopPropagation();
        event.nativeEvent.stopImmediatePropagation?.();

        const openNow = () => {
          try {
            doorway.openScheduling?.();
          } catch {
            // Swallow: navigation already prevented, so the user sees nothing.
            // They can re-tap the floating launcher.
          }
        };

        if (doorway.isLoaded && typeof doorway.openScheduling === 'function') {
          openNow();
        } else if (typeof doorway.addOnLoadHandler === 'function') {
          doorway.addOnLoadHandler(openNow);
        }
        // else: doorway exists but no usable API path — accept a silent no-op
        // rather than redirecting away from the user's current page.
      }

      setMenuOpen(false); // close the mobile slide-out sheet regardless
    },
    [],
  );

  // ---- Contrast-driven logo-variant selection ----
  // The sidebar paints itself in `--vibe-color-primary` and renders its text
  // in the runtime's authoritative `--vibe-color-text-on-primary` token (set
  // on the <header>/mobile-bar inline styles below). We still mirror that
  // contrast decision in JS here purely to pick the matching logo asset
  // (mono-white on dark/medium primaries, mono-black on light ones) — the
  // logic matches the tokens generator's 0.55 luminance threshold.
  const primaryColor = usePrimaryColor();
  const textOnPrimary = getContrastTextColor(primaryColor);
  const useWhiteOnPrimary = textOnPrimary === TEXT_ON_PRIMARY_LIGHT;

  // Logo default field follows the contrast decision (mono white on dark
  // primary, mono black on light primary), so the logo always reads against
  // the sidebar background. Editor overrides still win.
  const runtimeLogoSlotDef: SlotGroupDefinition = useWhiteOnPrimary
    ? logoSlotDef
    : {
        ...logoSlotDef,
        fields: {
          ...logoSlotDef.fields,
          image: { ...logoSlotDef.fields.image, defaultField: 'property_mono_black_logo' },
        },
      };

  // Property record — used for the Interactive Map nav gate and social links.
  const property = useProperty() as Record<string, unknown>;

  // Virtual tours — controls nav-link visibility
  const virtualTours = useCollectionBinding<VirtualTour>('header.virtual_tours', { collection: 'virtual_tours' });
  const hasVirtualTours = (virtualTours || []).some((t) => t.virtual_tour_url);

  // Interactive Map — requires an Engrain sitemap ID (mirrors SSG hide logic).
  // The Engrain Sightmap block embeds the map from a per-property sightmap ID
  // (or full embed URL). We accept both the canonical "sightmap" spelling and
  // the "sitemap" alternate — the block reads the same field pair — so the page
  // stays visible whenever a usable Engrain map is configured.
  const engrainSitemapId =
    (property?.custom_engrain_sightmap_id as string | null | undefined) ??
    (property?.custom_engrain_sitemap_id as string | null | undefined);
  const engrainSitemapUrl =
    (property?.custom_engrain_sightmap_url as string | null | undefined) ??
    (property?.custom_engrain_sitemap_url as string | null | undefined);
  // Editor-facing override: when this token (or its bound field) contains any
  // value, expose the Interactive Map page regardless of the Engrain fields.
  const interactiveMapShowIfExists = useContentToken('header.interactive_map_show_if_exists');
  const hasInteractiveMap =
    !!(engrainSitemapId && String(engrainSitemapId).trim()) ||
    !!(engrainSitemapUrl && String(engrainSitemapUrl).trim()) ||
    !!(interactiveMapShowIfExists && interactiveMapShowIfExists.trim());

  // Content tokens + slot groups
  const phone = useContentToken('header.phone');
  const logoRaw = useSlotGroup('header.logo', runtimeLogoSlotDef);
  const applyNowLink = useSlotGroup('header.apply_now_link', applyNowLinkDef);
  const scheduleTourLink = useSlotGroup('header.schedule_tour_link', scheduleTourLinkDef);

  // When the "Schedule a Tour" CTA already points at /contact-us, hide the
  // inherited Contact Us nav entry so we don't surface duplicate contact-us
  // CTAs in the sidebar.
  const hideContactNav = normalizePath(scheduleTourLink.url) === CONTACT_US_PATH;

  const navLinks = useMemo(
    () => buildNavLinks(hasVirtualTours, hasInteractiveMap, hideContactNav),
    [hasVirtualTours, hasInteractiveMap, hideContactNav],
  );

  // The Apply Now CTA mirrors the `apply-now` page's on/off state: we only
  // surface the pill when that page is actually in use. The page must exist in
  // pages.json AND not be turned off (visible === false). When the page isn't
  // used at all — removed from pages.json or disabled — we drop the pill so we
  // never link to a page that isn't there.
  const applyNowPage = (pagesData as unknown as Record<string, PageEntry>)['apply-now'];
  const applyNowOverride = (pagesOverride as unknown as Record<string, { visible?: boolean }>)['apply-now'];
  const applyNowVisible = applyNowOverride?.visible ?? applyNowPage?.visible;
  const showApplyNow = !!applyNowPage && applyNowVisible !== false;

  const residentAccessLink = useSlotGroup('header.resident_access_link', residentAccessLinkDef);
  const addressLink = useSlotGroup('header.address_link', addressLinkDef);
  const propertyAddressUrl = usePropertyAddressUrl();

  // Office hours
  const officeHours = useCollectionBinding<OfficeHour>('header.office_hours', { collection: 'office_hours' });
  const groupedHours = officeHours && officeHours.length > 0 ? groupOfficeHours(officeHours) : [];

  // Social links — read directly from per-property URL fields.
  const socialLinks: SocialEntry[] = useMemo(() => {
    const candidates: Array<{ field: string; icon: IconDefinition; label: string }> = [
      { field: 'property_facebook_url', icon: faFacebookF, label: 'Facebook' },
      { field: 'property_instagram_url', icon: faInstagram, label: 'Instagram' },
      { field: 'property_twitter_url', icon: faXTwitter, label: 'Twitter' },
      { field: 'property_youtube_url', icon: faYoutube, label: 'YouTube' },
      { field: 'property_tiktok_url', icon: faTiktok, label: 'TikTok' },
      { field: 'property_yelp_url', icon: faYelp, label: 'Yelp' },
      { field: 'property_google_business_url', icon: faGoogle, label: 'Google' },
    ];
    return candidates
      .map((c) => ({ url: property?.[c.field] as string | undefined, icon: c.icon, label: c.label }))
      .filter((s): s is SocialEntry => typeof s.url === 'string' && s.url.trim().length > 0);
  }, [property]);

  // Google Fonts — theme heading/body fonts matching the Aura at Westchase
  // original: Red Rose (headings, --vibe-font-heading) + Poppins (body,
  // --vibe-font-body). Loaded globally here so every component's font-heading /
  // font-body utilities actually render in the intended faces.
  useEffect(() => {
    if (document.querySelector('link[data-vibe-font="theme-aura"]')) return;
    const preconnect1 = document.createElement('link');
    preconnect1.rel = 'preconnect';
    preconnect1.href = 'https://fonts.googleapis.com';
    document.head.appendChild(preconnect1);
    const preconnect2 = document.createElement('link');
    preconnect2.rel = 'preconnect';
    preconnect2.href = 'https://fonts.gstatic.com';
    preconnect2.crossOrigin = 'anonymous';
    document.head.appendChild(preconnect2);
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&family=Red+Rose:wght@300;400;500;600;700&display=swap';
    link.setAttribute('data-vibe-font', 'theme-aura');
    document.head.appendChild(link);
  }, []);

  // Lock body scroll when the mobile sheet is open
  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  // Mobile top bar sits on a white background, so its logo must read against
  // white regardless of the contrast-picked sidebar variant. Prefer the
  // mono-black asset, falling back to the resolved sidebar logo.
  const mobileLogoUrl =
    (property?.property_mono_black_logo as string | undefined) || logoRaw.image;

  const logoNode = logoRaw.image ? (
    <VibesitesImage
      url={logoRaw.image}
      alt={logoRaw.alt || 'Community Logo'}
      className="w-full h-auto max-w-[220px] object-contain mx-auto"
    />
  ) : (
    <div className="font-heading text-2xl font-semibold">The Rise</div>
  );

  // ---- Shared sidebar body (identical on desktop + inside the mobile sheet) ----
  const sidebarBody = (
    <div className="rise-sidebar-inner flex flex-col items-center px-6 py-8">
      {/* Logo */}
      <EditableSlotGroup groupPath="header.logo" definition={runtimeLogoSlotDef}>
        <a href="/" onClick={closeMobileMenu} className="block mb-9">
          {logoNode}
        </a>
      </EditableSlotGroup>

      {/* Navigation */}
      <nav className="flex flex-col items-center gap-4 w-full mb-9" aria-label="Main navigation">
        {navLinks.map((link) => (
          <React.Fragment key={link.href}>
            <a href={link.href} onClick={closeMobileMenu} className="rise-side-link">
              {link.label}
            </a>
            {link.children?.map((child) => (
              <a
                key={child.href}
                href={child.href}
                onClick={closeMobileMenu}
                className="rise-side-link rise-side-sublink"
              >
                {child.label}
              </a>
            ))}
          </React.Fragment>
        ))}
      </nav>

      {/* Primary CTAs */}
      <div className="flex flex-col gap-3 w-full max-w-[270px] mb-8">
        <EditableSlotGroup groupPath="header.schedule_tour_link" definition={scheduleTourLinkDef}>
          <a
            href={scheduleTourLink.url || '/schedule-a-tour'}
            target={scheduleTourLink._target || '_self'}
            onClick={handleScheduleTourClick}
            className="rise-pill rise-pill-filled"
          >
            {scheduleTourLink.label || 'Schedule a Tour'}
          </a>
        </EditableSlotGroup>
        {showApplyNow && (
          <EditableSlotGroup groupPath="header.apply_now_link" definition={applyNowLinkDef}>
            <a
              href={applyNowLink.url || '/apply-now'}
              target={applyNowLink._target || '_self'}
              onClick={closeMobileMenu}
              className="rise-pill rise-pill-outline"
            >
              {applyNowLink.label || 'Apply Now'}
            </a>
          </EditableSlotGroup>
        )}
        <EditableSlotGroup groupPath="header.resident_access_link" definition={residentAccessLinkDef}>
          <a
            href={residentAccessLink.url || '/resident-access'}
            target={residentAccessLink._target || '_self'}
            onClick={closeMobileMenu}
            className="rise-pill rise-pill-outline"
          >
            {residentAccessLink.label || 'Resident Access'}
          </a>
        </EditableSlotGroup>
      </div>

      {/* Call Us */}
      <div className="w-full max-w-[270px] text-center mb-7">
        <p className="rise-side-label mb-3">Call Us</p>
        <a
          href={`tel:${(phone || '').replace(/[^+\d]/g, '')}`}
          className="dniphone dniphonehref rise-pill rise-pill-outline"
          aria-label="Call us"
        >
          <EditableToken
            path="header.phone"
            defaultField="property_call_tracking_phone"
            defaultValue="(555) 555-5555"
            as="span"
          >
            {formatPhone(phone)}
          </EditableToken>
        </a>
      </div>

      <hr className="rise-side-divider" />

      {/* Office Hours */}
      <div className="w-full max-w-[300px] text-center my-7">
        <p className="rise-side-label mb-4">Office Hours</p>
        {groupedHours.length > 0 ? (
          <dl className="space-y-2.5 w-full">
            {groupedHours.map((group, index) => (
              <div key={index} className="flex justify-between gap-4 rise-side-hours-row">
                <dt>{group.label}</dt>
                <dd className="text-right">{group.hours}</dd>
              </div>
            ))}
          </dl>
        ) : (
          <p className="rise-side-muted text-sm">Office hours will be displayed here.</p>
        )}
      </div>

      <hr className="rise-side-divider" />

      {/* Visit Us */}
      <div className="w-full max-w-[270px] text-center my-7">
        <p className="rise-side-label mb-3">Visit Us</p>
        <EditableSlotGroup groupPath="header.address_link" definition={addressLinkDef}>
          <a
            href={addressLink.url || propertyAddressUrl || '#'}
            target={addressLink._target || '_blank'}
            rel="noopener noreferrer"
            className="rise-pill rise-pill-outline rise-pill-multiline"
            aria-label="View on Google Maps"
          >
            <MapPinIcon className="w-4 h-4 flex-shrink-0" />
            <span>{addressLink.address || '123 Main Street, Atlanta, GA 30313'}</span>
          </a>
        </EditableSlotGroup>
      </div>

      {/* Follow Us */}
      {socialLinks.length > 0 && (
        <>
          <hr className="rise-side-divider" />
          <div className="w-full text-center mt-7">
            <p className="rise-side-label mb-4">Follow Us</p>
            <div className="flex flex-wrap justify-center gap-3">
              {socialLinks.map((s) => (
                <a
                  key={s.label}
                  href={s.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={s.label}
                  className="rise-social-icon"
                >
                  <FontAwesomeIcon icon={s.icon} className="w-4 h-4" />
                </a>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );

  return (
    <>
      {/* ============================================================== */}
      {/* Mobile top bar — hamburger (left) + logo. Hidden on lg+.       */}
      {/* ============================================================== */}
      <div
        className="rise-mobile-bar lg:hidden fixed left-0 right-0 z-[55] flex items-center justify-center h-16 px-4"
        style={{ backgroundColor: '#ffffff', color: '#1a1a1a' }}
      >
        <button
          onClick={() => setMenuOpen(!menuOpen)}
          className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center flex-shrink-0"
          aria-label={menuOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={menuOpen}
          aria-controls="rise-sidebar"
        >
          <span className={`rise-burger-bar ${menuOpen ? 'rotate-45 translate-y-0' : '-translate-y-2'}`} />
          <span className={`rise-burger-bar ${menuOpen ? 'opacity-0' : ''}`} />
          <span className={`rise-burger-bar ${menuOpen ? '-rotate-45 translate-y-0' : 'translate-y-2'}`} />
        </button>
        <a href="/" className="flex-shrink-0">
          {mobileLogoUrl ? (
            <VibesitesImage
              url={mobileLogoUrl}
              alt={logoRaw.alt || 'Community Logo'}
              className="h-14 w-auto max-w-[240px] object-contain"
            />
          ) : (
            <span className="font-heading text-lg font-semibold">The Rise</span>
          )}
        </a>
      </div>

      {/* Mobile backdrop */}
      <div
        className={`rise-sidebar-backdrop lg:hidden fixed inset-0 z-[59] bg-black/50 transition-opacity duration-300 ${
          menuOpen ? 'opacity-100 visible' : 'opacity-0 invisible pointer-events-none'
        }`}
        onClick={closeMobileMenu}
        aria-hidden="true"
      />

      {/* ============================================================== */}
      {/* The sidebar — fixed left, always visible on lg+, slide-out      */}
      {/* sheet on mobile.                                                */}
      {/* ============================================================== */}
      <header
        id="rise-sidebar"
        className={`rise-sidebar fixed left-0 bottom-0 z-[60] overflow-y-auto lg:translate-x-0 ${
          menuOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        style={{ backgroundColor: 'var(--vibe-color-primary)', color: 'var(--vibe-color-text-on-primary)' }}
      >
        {sidebarBody}
      </header>

      {/* Schema-lint anchor — hidden, never interactive. Surfaces the
          Interactive Map visibility token for sidebar editing; the token is a
          config flag (not rendered text), so it has no visible wrapper. */}
      <EditableToken
        path="header.interactive_map_show_if_exists"
        as="span"
        className="hidden"
        style={{ display: 'none' }}
      >
        {''}
      </EditableToken>

      <style>{`
        :root {
          --rise-sidebar-width: 300px;
          --rise-mobile-bar-height: 4rem;
        }

        .rise-sidebar {
          width: var(--rise-sidebar-width);
          font-family: var(--vibe-font-body);
          /* The SpecialOfferBanner is a full-width fixed strip pinned to the
             very top (it publishes its live height to --offer-banner-height,
             collapsing to 0 when there is no active offer / it is dismissed /
             scrolled away). Rather than offsetting the sidebar's top by that
             height, anchor the sidebar to the very top edge and reserve the
             banner's height as padding-top. This keeps the sidebar's
             primary-colored background painted across the full left column at
             all times — including the strip the banner occupies — so when the
             banner collapses (height -> 0) the vacated strip reveals the
             sidebar's primary fill instead of the page's dark backdrop. The
             banner sits later in the DOM at the same z-index, so it still
             paints over this strip while it is visible. padding-top transitions
             in lockstep with the banner's max-height ease; transform keeps the
             mobile slide-out. */
          top: 0;
          padding-top: var(--offer-banner-height, 0px);
          transition: transform 0.3s ease-in-out,
                      padding-top 0.5s cubic-bezier(0.4, 0, 0.2, 1);
          /* Slight shadow lifting the sidebar above the page content. */
          box-shadow: 4px 0 24px rgba(0, 0, 0, 0.14);
        }

        .rise-mobile-bar {
          /* Sit below the special-offer banner, mirroring the sidebar. */
          top: var(--offer-banner-height, 0px);
          transition: top 0.5s cubic-bezier(0.4, 0, 0.2, 1);
          box-shadow: 0 2px 12px rgba(0, 0, 0, 0.14);
        }

        /* Offset the page content right of the fixed sidebar on desktop.
           The sidebar is a direct child of .vibesites-layout and uses
           position:fixed, so it stays pinned to the viewport while the
           main + footer flow regions are pushed over. */
        @media (min-width: 1024px) {
          .vibesites-layout { padding-left: var(--rise-sidebar-width); }
        }
        /* On mobile the sidebar is an overlay sheet — no horizontal offset.
           Instead push content below the fixed hamburger top bar, which now
           itself sits below the special-offer banner — so clear both. */
        @media (max-width: 1023.98px) {
          .vibesites-main {
            padding-top: calc(var(--offer-banner-height, 0px) + var(--rise-mobile-bar-height));
            transition: padding-top 0.5s cubic-bezier(0.4, 0, 0.2, 1);
          }
        }

        /* Anchors / buttons inside the sidebar + mobile bar inherit the
           contrast-picked on-primary text color instead of the UA defaults. */
        .rise-sidebar a,
        .rise-sidebar button,
        .rise-mobile-bar a,
        .rise-mobile-bar button {
          color: inherit;
        }

        /* Hamburger bars */
        .rise-burger-bar {
          position: absolute;
          display: block;
          width: 1.5rem;
          height: 2px;
          background-color: currentColor;
          transition: transform 0.3s ease, opacity 0.3s ease;
        }

        /* Navigation links */
        .rise-side-link {
          font-family: var(--vibe-font-body);
          font-size: 13px;
          font-weight: 600;
          letter-spacing: 0.16em;
          text-transform: uppercase;
          line-height: 1.2;
          opacity: 0.95;
          /* Animated underline on hover — drawn via text-decoration so it
             rides the text baseline. Underline color uses currentColor, which
             resolves to --vibe-color-text-on-primary on the sidebar. */
          text-decoration-line: underline;
          text-decoration-color: transparent;
          text-decoration-thickness: 1.5px;
          text-underline-offset: 6px;
          transition: opacity 0.2s ease, text-decoration-color 0.25s ease, text-underline-offset 0.25s ease;
        }
        .rise-side-link:hover {
          opacity: 1;
          text-decoration-color: currentColor;
          text-underline-offset: 4px;
        }
        .rise-side-sublink {
          font-size: 11px;
          opacity: 0.7;
        }

        /* Section labels (Call Us / Office Hours / Visit Us / Follow Us) */
        .rise-side-label {
          font-family: var(--vibe-font-body);
          font-size: 11px;
          font-weight: 600;
          letter-spacing: 0.22em;
          text-transform: uppercase;
          opacity: 0.85;
        }
        .rise-side-muted { opacity: 0.5; }

        /* Office hours rows */
        .rise-side-hours-row {
          font-size: 13px;
          opacity: 0.85;
        }

        /* Pill buttons */
        .rise-pill {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          width: 100%;
          padding: 0.75rem 1.25rem;
          border-radius: 9999px;
          font-family: var(--vibe-font-body);
          font-size: 12px;
          font-weight: 600;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          line-height: 1.3;
          text-align: center;
          /* Animated underline on hover, matching the nav links. currentColor
             resolves to --vibe-color-text-on-secondary on the filled pill and
             --vibe-color-text-on-primary on the outline pills. */
          text-decoration-line: underline;
          text-decoration-color: transparent;
          text-decoration-thickness: 1.5px;
          text-underline-offset: 4px;
          transition: background-color 0.25s ease, color 0.25s ease, border-color 0.25s ease, transform 0.2s ease, text-decoration-color 0.25s ease, text-underline-offset 0.25s ease;
        }
        .rise-pill:hover {
          transform: translateY(-1px);
          text-decoration-color: currentColor;
          text-underline-offset: 3px;
        }
        .rise-pill-multiline {
          white-space: normal;
        }

        /* Scoped under .rise-sidebar so the on-secondary text color (0,2,0)
           outranks the .rise-sidebar a color:inherit rule (0,1,1), which
           would otherwise force the filled pill to inherit the on-primary
           color and render unreadable on light secondary tones. */
        .rise-sidebar .rise-pill-filled {
          background-color: var(--vibe-color-secondary);
          color: var(--vibe-color-text-on-secondary, #fff);
          border: 1.5px solid var(--vibe-color-secondary);
        }
        .rise-sidebar .rise-pill-filled:hover {
          background-color: color-mix(in srgb, var(--vibe-color-secondary) 85%, black);
          border-color: color-mix(in srgb, var(--vibe-color-secondary) 85%, black);
        }

        .rise-pill-outline {
          background-color: transparent;
          border: 1.5px solid currentColor;
          color: inherit;
        }
        .rise-pill-outline:hover {
          background-color: color-mix(in srgb, currentColor 12%, transparent);
        }

        /* Dividers */
        .rise-side-divider {
          width: 100%;
          border: 0;
          border-top: 1px solid color-mix(in srgb, currentColor 22%, transparent);
        }

        /* Social icons */
        .rise-social-icon {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 2.25rem;
          height: 2.25rem;
          border-radius: 9999px;
          border: 1.5px solid color-mix(in srgb, currentColor 55%, transparent);
          transition: background-color 0.25s ease, color 0.25s ease, border-color 0.25s ease;
        }
        .rise-social-icon:hover {
          background-color: currentColor;
        }
        .rise-social-icon:hover svg {
          color: var(--vibe-color-primary);
        }

        /* Slim scrollbar for the sidebar overflow */
        .rise-sidebar { scrollbar-width: thin; scrollbar-color: color-mix(in srgb, currentColor 30%, transparent) transparent; }
        .rise-sidebar::-webkit-scrollbar { width: 6px; }
        .rise-sidebar::-webkit-scrollbar-thumb {
          background-color: color-mix(in srgb, currentColor 30%, transparent);
          border-radius: 9999px;
        }
      `}</style>
    </>
  );
}

// ============================================================================
// Content Schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'Header',
  displayName: 'Header',
  tokens: {
    'header.phone': {
      label: 'Phone Number',
      type: 'phone',
      defaultField: 'property_call_tracking_phone',
      defaultValue: '(555) 555-5555',
    },
    'header.interactive_map_show_if_exists': {
      label: 'Interactive Map Visibility',
      type: 'text',
      description: 'Expose interactive map page if this token or field contains a value',
      defaultValue: '',
    },
  },
  slotGroups: {
    'header.logo': logoSlotDef,
    'header.schedule_tour_link': scheduleTourLinkDef,
    'header.apply_now_link': applyNowLinkDef,
    'header.resident_access_link': residentAccessLinkDef,
    'header.address_link': addressLinkDef,
  },
  collectionBindings: {
    'header.virtual_tours': {
      label: 'Virtual Tours',
      collection: 'virtual_tours',
    },
    'header.office_hours': {
      label: 'Office Hours',
      collection: 'office_hours',
    },
  },
};

(Header as any).contentSchema = contentSchema;

export default Header;
